import React, { useContext, useMemo } from 'react';
import styles from './index.module.scss';
import { Button, Empty, Select } from '@maxtropy/components';
import { DoubleRightOutlined } from '@ant-design/icons';
import TimeAxis from './TimeAxis';
import StatisticsCard from './StatisticCard';
import Chart from './Chart';
import { ChartHourData, DateValue, DeviceRankItem, RightStatisticData } from '../../type';
import { isEmpty } from 'lodash-es';
import { ShareDataWrapperContext } from '@/pages/WgqAllMaps';

interface RightAreaProp {
  pageLabel?: string;
  dateRange?: DateValue;
  selectedDevice?: DeviceRankItem;
  deviceList?: DeviceRankItem[];
  onDeviceChange?: (device?: DeviceRankItem) => void;
  rightStatisticData?: RightStatisticData;
  chartHourData?: ChartHourData;
}

const isValidTimestamp = (ts?: number): ts is number => typeof ts === 'number' && Number.isFinite(ts);

const FlowChart = ({
  pageLabel,
  dateRange,
  selectedDevice,
  deviceList = [],
  onDeviceChange,
  rightStatisticData,
  chartHourData,
}: RightAreaProp) => {
  const { isStandardSize, loadMap } = useContext(ShareDataWrapperContext);
  const isNoMapLayout = !loadMap;
  const chartHeight = isNoMapLayout ? (isStandardSize ? 480 : 390) : undefined;
  const deviceOptions = useMemo(() => {
    return deviceList
      .filter(device => device.deviceId !== undefined)
      .map(device => ({
        label: device.deviceName || '--',
        value: device.deviceId,
      }));
  }, [deviceList]);

  const timeAxisData = useMemo(() => {
    return (
      rightStatisticData?.dataList?.flatMap(item => {
        if (!isValidTimestamp(item.ts)) return [];
        return [{ ts: item.ts, flag: !!item.flag }];
      }) || []
    );
  }, [rightStatisticData?.dataList]);

  const chartData = useMemo(() => {
    return (
      chartHourData?.dataList?.flatMap(item => {
        if (!isValidTimestamp(item.ts)) return [];
        const flow = item.flow ?? 0;
        const isLongFlow = !!item.flag;
        return [
          {
            time: item.ts,
            totalAmount: flow,
            longFlowAmount: isLongFlow ? flow : null,
            nonLongFlowAmount: isLongFlow ? null : flow,
            flag: isLongFlow,
            pressure: item.pressure,
          },
        ];
      }) || []
    );
  }, [chartHourData?.dataList]);
  return (
    <div className={`${styles.flowChart} ${isNoMapLayout ? styles.flowChartNoMap : ''}`}>
      <div className={styles.chartHeader}>
        <Select
          style={{ width: 180 }}
          value={selectedDevice?.deviceId}
          options={deviceOptions}
          placeholder="请选择"
          getPopupContainer={() => document.getElementById('full_screen_box') ?? document.body}
          onChange={value => {
            onDeviceChange?.(deviceList.find(device => device.deviceId === value));
          }}
        />

        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <span style={{ color: 'rgba(255, 255, 255, 0.65)', fontSize: 14 }}>
            统计周期：
            <span
              style={{
                fontWeight: 500,
                fontSize: 16,
                color: '#52E7FF',
              }}
            >
              {rightStatisticData?.days || '--'}天
            </span>
          </span>
          <Button
            type="link"
            onClick={() => {
              const params = new URLSearchParams();
              if (dateRange && dateRange[0] && dateRange[1]) {
                params.set('startDate', dateRange[0].format('YYYY-MM-DD'));
                params.set('endDate', dateRange[1].format('YYYY-MM-DD'));
              }
              window.open(`/longflow/monitoring/monitor?${params.toString()}`, '_blank');
            }}
            style={{ padding: '0 0 2px 0' }}
          >
            详情
            <DoubleRightOutlined />
          </Button>
        </div>
      </div>
      {isEmpty(selectedDevice) ? (
        <Empty style={{ marginTop: 100 }} />
      ) : (
        <>
          {/* 统计信息区域 */}
          <StatisticsCard pageLabel={pageLabel} statistics={rightStatisticData} />

          {/* 时间轴 */}
          <TimeAxis pageLabel={pageLabel} data={timeAxisData} />

          <Chart pageLabel={pageLabel} chartData={chartData} queryTimeRanges={dateRange} height={chartHeight} />
        </>
      )}
    </div>
  );
};

export default FlowChart;
